import { DataBarsRow } from "@/components/data-bars/DataBars.tsx";
import { filtersLibrary, periodsLibrary } from "@/components/filter/filter.types.ts";
import { FilterChoosePeriod } from "@/components/filter/FilterChoosePeriod.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { limitedColors } from "@/components/emoji/limited-colors.ts";
import { BarChart } from "@/components/reporting/BarChart.tsx";
import { KpiTile } from "@/components/reporting/KpiTile.tsx";
import { LineChart } from "@/components/reporting/LineChart.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import { DimensionBarsWidget } from "@/pages/reporting/components/DimensionBarsWidget.tsx";
import { ReportingWidget } from "@/pages/reporting/components/ReportingWidget.tsx";
import {
  buildNullableWidgetDataBarsRows,
  widgetDimensionToNullableBarData,
  widgetPointEntityIds,
  widgetSeriesToLineData,
  widgetDimensionToBarData,
} from "@/pages/reporting/components/widget-chart.adapter.ts";
import { metricLabelSuffix } from "@/pages/reporting/components/metric-label.ts";
import { useReportViewFilter } from "@/pages/reporting/components/use-report-view-filter.ts";
import { PeopleAnalyticsCacheStatus } from "@/pages/reporting/employee/people-analytics/components/PeopleAnalyticsCacheStatus.tsx";
import { HeadcountMetricSelector } from "@/pages/reporting/employee/headcount/HeadcountMetricSelector.tsx";
import { useHeadcountWidget } from "@/pages/reporting/employee/headcount/use-headcount-widget.ts";
import {
  SplitBySelector,
  SplitByValue,
} from "@/pages/reporting/employee/headcount/SplitBySelector.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiAnalyticsEmployeesFilter } from "@/types/analytics/analytics.employee.types.ts";
import { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  AnalyticReportTypes,
  ApiAnalyticsAggregationDate,
} from "@/types/analytics/analytics.types.ts";
import {
  ApiWidgetData,
  ApiWidgetDimension,
  ApiWidgetFilter,
  ApiWidgetFilterField,
  ApiWidgetMetric,
  ApiWidgetPeriodMode,
  ApiWidgetPreviewInput,
  ApiWidgetType,
} from "@/types/reporting/reporting.types.ts";
import {
  ApiAggregationDate,
  Box,
  Page,
  Skeleton,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { isFuture, isPast, isToday } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { twMerge } from "tailwind-merge";

interface HeadcountV2Filter extends ApiAnalyticsEmployeesFilter {
  department_ids?: number[];
  division_ids?: number[];
  location_ids?: number[];
  employment_type_ids?: number[];
  employment_contract_ids?: number[];
}

const formatPercent = (value: number) => `${value.toFixed(2)}%`;
const formatCount = (value: number) => `${Math.round(value)}`;

const previewLifecycleStatuses = Object.values(ApiEmployeeLifecycleStatus);

export const EmployeeHeadcountReport = () => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { canIView } = usePermissions();
  const [metric, setMetric] = useState<ApiWidgetMetric>(ApiWidgetMetric.headcount);
  const isPercentMetric = metric !== ApiWidgetMetric.headcount;
  const formatMetricValue = (value: number | null | undefined): string =>
    isPercentMetric ? `${parseFloat(`${value ?? 0}`).toFixed(2)}%` : `${value ?? 0}`;

  const reportTitle = t("reporting:headcount_report_title");

  useTitle(reportTitle, t("common:reports"));

  const library = filtersLibrary();
  const periodLibrary = periodsLibrary();

  const baseEmployeeFilters = [
    library.job_profile_ids,
    library.job_role_ids,
    library.department_ids,
    library.division_ids,
    library.location_ids,
    library.employment_type_ids,
    library.employment_contract_ids,
  ];

  const {
    filter: { filter },
    filterButton,
    config,
  } = useAppFilter<HeadcountV2Filter>({
    filters: [
      library.aggregation_date_mode,
      { ...library.date_from, hideInFilter: true },
      { ...library.date_to, hideInFilter: true },
      { ...library.period, defaultValue: "rolling_year" },
      ...baseEmployeeFilters,
    ],
    source: AnalyticReportTypes.employeesHeadcount,
  });

  const viewFilterApplied = useReportViewFilter(baseEmployeeFilters);

  const { formatPeriodDates, renderDate } = useFormatter();

  const widgetFilters = useMemo<ApiWidgetFilter[]>(() => {
    const result: ApiWidgetFilter[] = [];
    const push = (field: ApiWidgetFilterField, ids?: number[]) => {
      if (ids && ids.length) result.push({ field, ids });
    };
    push(ApiWidgetFilterField.job_profile_ids, filter.job_profile_ids);
    push(ApiWidgetFilterField.job_role_ids, filter.job_role_ids);
    push(ApiWidgetFilterField.department_ids, filter.department_ids);
    push(ApiWidgetFilterField.division_ids, filter.division_ids);
    push(ApiWidgetFilterField.location_ids, filter.location_ids);
    push(ApiWidgetFilterField.employment_type_ids, filter.employment_type_ids);
    push(ApiWidgetFilterField.employment_contract_ids, filter.employment_contract_ids);
    return result;
  }, [filter]);

  const dateGrain = filter.aggregation_date_mode ?? ApiAnalyticsAggregationDate.month;
  const ready = viewFilterApplied && !!filter.date_from && !!filter.date_to;

  const baseQuery = useMemo(
    () => ({
      filters: widgetFilters,
      period: {
        mode: ApiWidgetPeriodMode.absolute,
        date_from: filter.date_from,
        date_to: filter.date_to,
      },
    }),
    [widgetFilters, filter.date_from, filter.date_to]
  );

  const kpi = (metric: ApiWidgetMetric): ApiWidgetPreviewInput => ({
    widget_type: ApiWidgetType.kpi,
    period: baseQuery.period,
    query: { metrics: [metric], filters: baseQuery.filters },
  });

  const line = (metrics: ApiWidgetMetric | ApiWidgetMetric[]): ApiWidgetPreviewInput => ({
    widget_type: ApiWidgetType.line_chart,
    period: baseQuery.period,
    query: {
      metrics: Array.isArray(metrics) ? metrics : [metrics],
      filters: baseQuery.filters,
      date_grain: dateGrain,
    },
  });

  const dimension = (metric: ApiWidgetMetric, dim: ApiWidgetDimension): ApiWidgetPreviewInput => ({
    widget_type: ApiWidgetType.bar_chart,
    period: baseQuery.period,
    query: { metrics: [metric], dimensions: [dim], filters: baseQuery.filters },
  });

  const headcountKpi = useHeadcountWidget(kpi(ApiWidgetMetric.headcount), ready);
  const growthKpi = useHeadcountWidget(kpi(ApiWidgetMetric.headcount_growth_rate), ready);
  const hiresKpi = useHeadcountWidget(kpi(ApiWidgetMetric.hires), ready);
  const terminationsKpi = useHeadcountWidget(kpi(ApiWidgetMetric.terminations), ready);
  // const turnoverKpi = useHeadcountWidget(kpi(ApiWidgetMetric.turnover_rate), ready);

  const [metricSplitBy, setMetricSplitBy] = useState<SplitByValue>(null);
  const [growthRateSplitBy, setGrowthRateSplitBy] = useState<SplitByValue>(null);
  const [managerRatioSplitBy, setManagerRatioSplitBy] = useState<SplitByValue>(null);
  const [managersCountSplitBy, setManagersCountSplitBy] = useState<SplitByValue>(null);
  const [joinersLeaversSplitBy, setJoinersLeaversSplitBy] = useState<SplitByValue>(null);

  const lineOrBar = (metric: ApiWidgetMetric, splitBy: SplitByValue): ApiWidgetPreviewInput =>
    splitBy
      ? {
          widget_type: ApiWidgetType.bar_chart,
          period: baseQuery.period,
          query: {
            metrics: [metric],
            dimensions: [splitBy],
            filters: baseQuery.filters,
          },
        }
      : line(metric);

  const metricLine = useHeadcountWidget(lineOrBar(metric, metricSplitBy), ready);
  const joinersLeaversData = useHeadcountWidget(
    {
      widget_type: joinersLeaversSplitBy ? ApiWidgetType.bar_chart : ApiWidgetType.line_chart,
      period: baseQuery.period,
      query: {
        metrics: [ApiWidgetMetric.hires, ApiWidgetMetric.terminations],
        dimensions: joinersLeaversSplitBy ? [joinersLeaversSplitBy] : undefined,
        filters: baseQuery.filters,
        date_grain: joinersLeaversSplitBy ? undefined : dateGrain,
      },
    },
    ready
  );
  const managerRatioLine = useHeadcountWidget(
    lineOrBar(ApiWidgetMetric.manager_to_employee_ratio, managerRatioSplitBy),
    ready
  );
  const managersCountLine = useHeadcountWidget(
    lineOrBar(ApiWidgetMetric.managers_count, managersCountSplitBy),
    ready
  );
  const growthRateLine = useHeadcountWidget(
    lineOrBar(ApiWidgetMetric.headcount_growth_rate, growthRateSplitBy),
    ready
  );

  const byJobProfiles = useHeadcountWidget(
    dimension(metric, ApiWidgetDimension.job_profile),
    ready
  );
  const byJobRoles = useHeadcountWidget(dimension(metric, ApiWidgetDimension.job_role), ready);
  const byDepartments = useHeadcountWidget(dimension(metric, ApiWidgetDimension.department), ready);
  const byDivisions = useHeadcountWidget(dimension(metric, ApiWidgetDimension.division), ready);
  const byLocations = useHeadcountWidget(dimension(metric, ApiWidgetDimension.location), ready);
  const byTerminationLeaveTypes = useHeadcountWidget(
    dimension(ApiWidgetMetric.terminations, ApiWidgetDimension.termination_leave_type),
    ready
  );
  const byTerminationReasons = useHeadcountWidget(
    dimension(ApiWidgetMetric.terminations, ApiWidgetDimension.termination_reason),
    ready
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.reporting.viewed, {
      [analyticProperties.title]: AnalyticReportTypes.employeesHeadcount + " V2",
    });
  });

  const reportPeriodDates = useMemo(() => {
    if (!filter.date_from || !filter.date_to) return null;
    return formatPeriodDates({ dateStart: filter.date_from, dateEnd: filter.date_to });
  }, [filter.date_from, filter.date_to]);

  const isFuturePastToday = () => {
    const dateTo = filter.date_to;
    if (!dateTo) return null;
    if (isToday(dateTo)) return t("reporting:headcount_report_people_now");
    if (isPast(dateTo))
      return t("reporting:headcount_report_people_were") + " " + renderDate(dateTo);
    if (isFuture(dateTo))
      return t("reporting:headcount_report_people_future") + " " + renderDate(dateTo);
  };

  const metricLineData = useMemo(
    () =>
      metricSplitBy
        ? (isPercentMetric ? widgetDimensionToNullableBarData : widgetDimensionToBarData)(
            metricLine.data,
            t(`reporting:metric_${metric}`)
          )
        : widgetSeriesToLineData(metricLine.data, formatPeriodDates),
    [metricLine.data, metricSplitBy, metric]
  );
  const managerRatioLineData = useMemo(
    () =>
      managerRatioSplitBy
        ? widgetDimensionToNullableBarData(
            managerRatioLine.data,
            t("reporting:metric_manager_ratio")
          )
        : widgetSeriesToLineData(managerRatioLine.data, formatPeriodDates),
    [managerRatioLine.data, managerRatioSplitBy, formatPeriodDates, t]
  );
  const managersCountLineData = useMemo(
    () =>
      managersCountSplitBy
        ? widgetDimensionToBarData(managersCountLine.data, t("reporting:metric_managers"))
        : widgetSeriesToLineData(managersCountLine.data, formatPeriodDates),
    [managersCountLine.data, managersCountSplitBy, formatPeriodDates, t]
  );
  const growthRateLineData = useMemo(
    () =>
      growthRateSplitBy
        ? widgetDimensionToNullableBarData(growthRateLine.data, t("reporting:metric_growth"))
        : widgetSeriesToLineData(growthRateLine.data, formatPeriodDates),
    [growthRateLine.data, growthRateSplitBy]
  );

  const joinersLeaversChartData = useMemo(() => {
    const hiresPoints = joinersLeaversData.data?.series[0]?.points ?? [];
    const leaversPoints = joinersLeaversData.data?.series[1]?.points ?? [];
    const labels: string[] = [];
    const joiners: number[] = [];
    const leavers: number[] = [];
    hiresPoints.forEach((point, index) => {
      labels.push(
        joinersLeaversSplitBy
          ? point.label || ""
          : formatPeriodDates({
              dateStart: point.date_from ?? undefined,
              dateEnd: point.date_to ?? undefined,
            })?.title || ""
      );
      joiners.push(joinersLeaversSplitBy ? Number(point.value ?? 0) : point.entity_ids.length);
      leavers.push(
        joinersLeaversSplitBy
          ? Number(leaversPoints[index]?.value ?? 0)
          : (leaversPoints[index]?.entity_ids.length ?? 0)
      );
    });
    return { labels, joiners, leavers };
  }, [joinersLeaversData.data, joinersLeaversSplitBy]);

  const source = AnalyticReportTypes.employeesHeadcount + " V2";

  const buildSplitBarClickHandler = (data: ApiWidgetData | undefined, widgetSource: string) => {
    return (seriesIndex: number, pointIndex: number) => {
      const point = data?.series[seriesIndex]?.points[pointIndex];
      const ids = widgetPointEntityIds(data, pointIndex, seriesIndex);
      if (!point || !ids.length) return;

      openModal(PreviewEmployeesModal, {
        filter: {
          page_number: 1,
          employee_ids: ids,
          employee_lifecycle_statuses: previewLifecycleStatuses,
        },
        addColumns: [],
        modalTitle: point.label,
        source: `${source}, ${widgetSource}`,
      });
    };
  };

  const handleJoinersLeaversClick = (seriesIndex: number, pointIndex: number) => {
    const ids = widgetPointEntityIds(joinersLeaversData.data, pointIndex, seriesIndex);
    if (!ids.length) return;
    openModal(PreviewEmployeesModal, {
      filter: {
        page_number: 1,
        employee_ids: ids,
        employee_lifecycle_statuses: previewLifecycleStatuses,
      },
      addColumns: [],
      modalTitle: seriesIndex === 0 ? t("reporting:joiners") : t("reporting:leavers"),
      source: source + (seriesIndex === 0 ? ", Joiners" : ", Leavers"),
    });
  };

  const buildHeadcountDataBarsRows = (
    data: ApiWidgetData | undefined,
    source: string
  ): DataBarsRow[] => {
    const points = data?.series[0]?.points ?? [];
    const maxValue = Math.max(
      ...points.flatMap((point) => [point.entity_ids.length, point.comparison_entity_ids.length]),
      1
    );

    return points.map((point) => {
      const currentIds = point.entity_ids;
      const previousIds = point.comparison_entity_ids;
      const currentCount = currentIds.length;
      const previousCount = previousIds.length;
      const percent = (currentCount / maxValue) * 100;
      const deltaPercent = ((currentCount - previousCount) / maxValue) * 100;

      const handleOpenModal = (entityIds: number[], periodSuffix: string) => {
        if (!entityIds.length) return;
        openModal(PreviewEmployeesModal, {
          filter: {
            page_number: 1,
            employee_ids: entityIds,
            employee_lifecycle_statuses: previewLifecycleStatuses,
          },
          addColumns: [],
          modalTitle: point.label,
          source: source + periodSuffix,
        });
      };

      const renderTooltip = () => (
        <Stack gap={"xs"}>
          <Stack direction={"horizontal"} gap={"xs"}>
            <Text size={"sm"}>{t("reporting:tooltip_current_period")}:</Text>
            <Text
              size={"sm"}
              className={twMerge(currentCount > 0 && "underline cursor-pointer hover:text-brand")}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(currentIds, ", Current");
              }}
            >
              {t("plurals:employees_count", { count: currentCount })}
            </Text>
          </Stack>
          <Stack direction={"horizontal"} gap={"xs"}>
            <Text size={"sm"}>{t("reporting:tooltip_previous_period")}:</Text>
            <Text
              size={"sm"}
              className={twMerge(previousCount > 0 && "underline cursor-pointer hover:text-brand")}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(previousIds, ", Previous");
              }}
            >
              {t("plurals:employees_count", { count: previousCount })}
            </Text>
          </Stack>
        </Stack>
      );

      return {
        title: point.label,
        value: currentCount,
        sortValue: currentCount,
        valueDelta: currentCount - previousCount,
        percent,
        percentDelta: deltaPercent,
        emoji: null,
        tooltip: renderTooltip(),
        onClick: currentCount > 0 ? () => handleOpenModal(currentIds, ", Current") : undefined,
      };
    });
  };

  const metricLabel = t(`reporting:metric_${metricLabelSuffix(metric)}`);

  const dimensionWidget = (
    titleKey: string,
    byKey: string,
    data: ApiWidgetData | undefined,
    suffix: string,
    metricDriven: boolean = true
  ) => {
    const fullSource = source + ", " + suffix;
    const caption = `${metricDriven ? metricLabel : t("reporting:leavers")} ${t(byKey)}`;
    const rows =
      metricDriven && isPercentMetric
        ? buildNullableWidgetDataBarsRows(data, formatMetricValue, (employeeIds, label) =>
            openModal(PreviewEmployeesModal, {
              filter: {
                page_number: 1,
                employee_ids: employeeIds,
                employee_lifecycle_statuses: previewLifecycleStatuses,
              },
              addColumns: [],
              modalTitle: label,
              source: fullSource,
            })
          )
        : buildHeadcountDataBarsRows(data, fullSource);
    return <DimensionBarsWidget title={t(titleKey)} caption={caption} rows={rows} />;
  };

  return (
    <Box className={"bg-light/80 h-full overflow-y-auto"}>
      <Page.Header title={reportTitle}>
        <Stack
          direction={"horizontal"}
          gap={"sm"}
          items={"center"}
          className={"overflow-hidden flex-wrap"}
        >
          {canIView(ApiPermission.reporting_people_analytics) && <PeopleAnalyticsCacheStatus />}
          <HeadcountMetricSelector value={metric} onChange={setMetric} />
          <FilterChoosePeriod
            availableAggregationDateModes={[
              ApiAggregationDate.year,
              ApiAggregationDate.quarter,
              ApiAggregationDate.month,
              ApiAggregationDate.week,
            ]}
            staticPeriods={[
              periodLibrary.rolling_year,
              periodLibrary.year_to_date,
              periodLibrary.last_year_0,
              periodLibrary.last_year_2,
              periodLibrary.last_year_3,
            ]}
            filtersConfig={config}
          />
          {filterButton()}
        </Stack>
      </Page.Header>

      <Page.Content>
        {headcountKpi.isLoading && <Skeleton className={"w-full h-10"} />}
        {headcountKpi.data && (
          <Stack gap={"md"}>
            {metric === ApiWidgetMetric.headcount ? (
              <Text size={"6xl"} className={"font-semibold whitespace-pre pt-10 pb-4"}>
                <>
                  <Text>{t("plurals:people", { count: headcountKpi.data.value ?? 0 })}</Text>
                  &nbsp;
                  <Text className={"text-secondary"}>{isFuturePastToday()}</Text>
                </>
              </Text>
            ) : (
              <></>
            )}
            <Box className={twMerge("grid md:grid-cols-4 gap-md py-lg")}>
              <KpiTile
                title={t("reporting:metric_headcount")}
                value={headcountKpi.data?.value}
                format={"integer"}
              />
              <KpiTile
                title={t("common:total_growth")}
                value={growthKpi.data?.value === null ? "-" : growthKpi.data?.value}
                format={
                  growthKpi.data?.value === null
                    ? undefined
                    : (value) => `${value >= 0 ? "+" : ""} ${value.toFixed(2)}%`
                }
                caption={reportPeriodDates?.title}
              />
              <KpiTile
                title={t("reporting:joiners")}
                value={hiresKpi.data?.value}
                format={"integer"}
              />
              <KpiTile
                title={t("reporting:leavers")}
                value={terminationsKpi.data?.value}
                format={"integer"}
              />
            </Box>

            <ReportingWidget
              title={
                metric === ApiWidgetMetric.headcount
                  ? t(
                      metricSplitBy
                        ? "reporting:headcount_for_period"
                        : "reporting:headcount_over_time"
                    )
                  : t(metricSplitBy ? "reporting:period_growth" : "reporting:growth_over_time")
              }
              tooltip={
                <Text size={"sm"}>
                  {t(
                    `reporting:headcount_report_${metric === ApiWidgetMetric.headcount ? "headcount" : "growth"}_chart_tooltip`
                  )}
                </Text>
              }
              actions={<SplitBySelector value={metricSplitBy} onChange={setMetricSplitBy} />}
              content={
                metricLine.data && metricLineData.labels.length > 0 ? (
                  metricSplitBy ? (
                    <BarChart
                      labels={metricLineData.labels}
                      series={
                        (metricLineData as ReturnType<typeof widgetDimensionToBarData>).series
                      }
                      palette={limitedColors}
                      formatValue={formatMetricValue}
                      onBarClick={buildSplitBarClickHandler(metricLine.data, metricLabel)}
                    />
                  ) : (
                    <LineChart
                      labels={metricLineData.labels}
                      series={[
                        {
                          data: (metricLineData as ReturnType<typeof widgetSeriesToLineData>).data,
                        },
                      ]}
                      formatTooltipValue={isPercentMetric ? formatMetricValue : undefined}
                      yScale={metric === ApiWidgetMetric.headcount ? "logarithmic" : "linear"}
                    />
                  )
                ) : (
                  <Skeleton className={"w-full h-56"} />
                )
              }
            />

            {metric === ApiWidgetMetric.headcount && (
              <ReportingWidget
                title={t(
                  growthRateSplitBy ? "reporting:period_growth" : "reporting:growth_over_time"
                )}
                tooltip={
                  <Text size={"sm"}>{t("reporting:headcount_report_growth_chart_tooltip")}</Text>
                }
                actions={
                  <SplitBySelector value={growthRateSplitBy} onChange={setGrowthRateSplitBy} />
                }
                content={
                  growthRateLine.data && growthRateLineData.labels.length > 0 ? (
                    growthRateSplitBy ? (
                      <BarChart
                        labels={growthRateLineData.labels}
                        series={
                          (growthRateLineData as ReturnType<typeof widgetDimensionToBarData>).series
                        }
                        palette={limitedColors}
                        formatValue={formatPercent}
                        onBarClick={buildSplitBarClickHandler(growthRateLine.data, "Growth rate")}
                      />
                    ) : (
                      <LineChart
                        labels={growthRateLineData.labels}
                        series={[
                          {
                            data: (growthRateLineData as ReturnType<typeof widgetSeriesToLineData>)
                              .data,
                          },
                        ]}
                        formatTooltipValue={formatPercent}
                      />
                    )
                  ) : (
                    <Skeleton className={"w-full h-56"} />
                  )
                }
              />
            )}

            <ReportingWidget
              title={t("reporting:joiners_and_leavers")}
              tooltip={
                <Text size={"sm"}>
                  {t("reporting:headcount_report_joiners_leavers_chart_tooltip")}
                </Text>
              }
              actions={
                <SplitBySelector
                  value={joinersLeaversSplitBy}
                  onChange={setJoinersLeaversSplitBy}
                />
              }
              content={
                joinersLeaversData.data && joinersLeaversChartData.labels.length > 0 ? (
                  <BarChart
                    labels={joinersLeaversChartData.labels}
                    series={[
                      {
                        label: t("reporting:metric_joiners"),
                        data: joinersLeaversChartData.joiners,
                        color: "--color-new",
                      },
                      {
                        label: t("reporting:metric_leavers"),
                        data: joinersLeaversChartData.leavers,
                        color: "--color-warning",
                      },
                    ]}
                    showLegend
                    formatValue={formatCount}
                    onBarClick={handleJoinersLeaversClick}
                  />
                ) : (
                  <Skeleton className={"w-full h-56"} />
                )
              }
            />

            {metric === ApiWidgetMetric.headcount && (
              <Box className="grid md:grid-cols-2 gap-md">
                <ReportingWidget
                  title={t(
                    managerRatioSplitBy
                      ? "reporting:manager_ratio_for_period"
                      : "reporting:manager_ratio_over_time"
                  )}
                  tooltip={
                    <Text size={"sm"}>
                      {t("reporting:headcount_report_manager_ratio_chart_tooltip")}
                    </Text>
                  }
                  actions={
                    <SplitBySelector
                      value={managerRatioSplitBy}
                      onChange={setManagerRatioSplitBy}
                    />
                  }
                  content={
                    managerRatioLine.data && managerRatioLineData.labels.length > 0 ? (
                      managerRatioSplitBy ? (
                        <BarChart
                          labels={managerRatioLineData.labels}
                          series={
                            (managerRatioLineData as ReturnType<typeof widgetDimensionToBarData>)
                              .series
                          }
                          palette={limitedColors}
                          formatValue={(value) => value.toFixed(2)}
                          onBarClick={buildSplitBarClickHandler(
                            managerRatioLine.data,
                            "Manager ratio"
                          )}
                        />
                      ) : (
                        <LineChart
                          labels={managerRatioLineData.labels}
                          series={[
                            {
                              data: (
                                managerRatioLineData as ReturnType<typeof widgetSeriesToLineData>
                              ).data,
                            },
                          ]}
                          formatTooltipValue={(value) => value.toFixed(2)}
                        />
                      )
                    ) : (
                      <Skeleton className={"w-full h-56"} />
                    )
                  }
                />
                <ReportingWidget
                  title={t(
                    managersCountSplitBy
                      ? "reporting:managers_for_period"
                      : "reporting:managers_over_time"
                  )}
                  tooltip={
                    <Text size={"sm"}>
                      {t("reporting:headcount_report_managers_chart_tooltip")}
                    </Text>
                  }
                  actions={
                    <SplitBySelector
                      value={managersCountSplitBy}
                      onChange={setManagersCountSplitBy}
                    />
                  }
                  content={
                    managersCountLine.data && managersCountLineData.labels.length > 0 ? (
                      managersCountSplitBy ? (
                        <BarChart
                          labels={managersCountLineData.labels}
                          series={
                            (managersCountLineData as ReturnType<typeof widgetDimensionToBarData>)
                              .series
                          }
                          palette={limitedColors}
                          formatValue={formatCount}
                          onBarClick={buildSplitBarClickHandler(managersCountLine.data, "Managers")}
                        />
                      ) : (
                        <LineChart
                          labels={managersCountLineData.labels}
                          series={[
                            {
                              data: (
                                managersCountLineData as ReturnType<typeof widgetSeriesToLineData>
                              ).data,
                            },
                          ]}
                        />
                      )
                    ) : (
                      <Skeleton className={"w-full h-56"} />
                    )
                  }
                />
              </Box>
            )}

            <Box className="flex gap-(--gap-md)">
              <Stack gap="md" className="flex-1">
                {dimensionWidget(
                  "core:job_profiles",
                  "reporting:by_job_profiles",
                  byJobProfiles.data,
                  "Job profiles"
                )}
                {dimensionWidget(
                  "core:divisions",
                  "reporting:by_divisions",
                  byDivisions.data,
                  "Divisions"
                )}
                {dimensionWidget(
                  "core:termination_leave_types",
                  "reporting:by_leave_types",
                  byTerminationLeaveTypes.data,
                  "Termination leave types",
                  false
                )}
              </Stack>
              <Stack gap="md" className="flex-1">
                {dimensionWidget(
                  "core:job_roles",
                  "reporting:by_job_roles",
                  byJobRoles.data,
                  "Job roles"
                )}
                {dimensionWidget(
                  "core:departments",
                  "reporting:by_departments",
                  byDepartments.data,
                  "Departments"
                )}
                {dimensionWidget(
                  "common:locations",
                  "reporting:by_locations",
                  byLocations.data,
                  "Locations"
                )}
                {dimensionWidget(
                  "core:termination_reasons",
                  "reporting:by_termination_reasons",
                  byTerminationReasons.data,
                  "Termination reasons",
                  false
                )}
              </Stack>
            </Box>
          </Stack>
        )}
      </Page.Content>
    </Box>
  );
};
